import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import { CloudUpload } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dispatch, SetStateAction, useCallback, useState, useRef } from "react";

import { cn, getResumeFileName } from "src/lib/utils";
import { IJob } from "src/models";
import { Button } from "src/shadcn/ui/button";
import { useUser } from "src/hooks";
import { Checkbox } from "src/shadcn/ui/checkbox";
import {
  uploadResumeFile,
  createJobFirstStep,
  activateResumeFile,
} from "src/services/job";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/shadcn/ui/table";

interface Props {
  currentJob: IJob;
  currentResumeId: string | null;
  setCurrentJob: Dispatch<SetStateAction<IJob | null>>;
  setCurrentResumeId: Dispatch<SetStateAction<string | null>>;
}

const YourResume: React.FC<Props> = ({
  currentJob,
  currentResumeId,
  setCurrentJob,
  setCurrentResumeId,
}) => {
  const { data: userProfile, refetch } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const backupResumeId = useRef<string | null>(null);

  // states
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // functions
  const handleUploadResumeFile = useCallback(
    async (file: File) => {
      setIsLoading(true);

      let userId = userProfile?.id;

      if (!userId) {
        const { data } = await refetch();
        userId = data?.id;
      }

      if (!currentJob?.id) {
        const newJob = await createJobFirstStep("", "", "", "", userId);

        if (newJob?.id) {
          const res = await uploadResumeFile(newJob.id, file);
          setIsLoading(false);

          if (res) {
            navigate(`/job/${newJob.id}`);
          }
        }
        return;
      }

      const res = await uploadResumeFile(currentJob?.id, file);
      setIsLoading(false);

      if (res) {
        setCurrentJob({ ...res });
      }
    },
    [userProfile],
  );

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles?.length > 1) {
      toast.error("Please upload only one file");
      return;
    }
    handleUploadResumeFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleNext = () => {
    if (!currentResumeId) {
      toast.error("Please select a resume file");
      return;
    }
    navigate(`${location.pathname}?step=2`);
  };

  const handleActivateResume = async (resumeId: string) => {
    setIsLoading(true);
    const res = await activateResumeFile(currentJob?.id, resumeId);
    setIsLoading(false);

    if (res) {
      backupResumeId.current = resumeId;
    } else {
      setCurrentResumeId(backupResumeId.current);
    }
  };

  const handleCheckedChange = (
    checked: boolean | "indeterminate",
    id: string,
  ) => {
    if (checked) {
      handleActivateResume(id);
      setCurrentResumeId(id);
    } else {
      setCurrentResumeId(null);
    }
  };

  return (
    <div className="w-full grow relative pb-14 flex items-center justify-center">
      <div className="w-full">
        {(!currentJob?.resumes || currentJob?.resumes?.length === 0) && (
          <div className="mt-20 mb-10">
            <p className="text-center text-lg font-bold">
              {isLoading ? "Uploading..." : "No Resume File"}
            </p>
          </div>
        )}

        {!isLoading && (
          <>
            <div className="flex flex-col items-center border-[#DBEBFF] border-2 bg-[#F6FAFF] rounded-xl p-4 py-6 max-w-[600px] mx-auto">
              <div
                {...getRootProps()}
                className={cn("file-upload w-fit h-full p-4 cursor-pointer")}
              >
                <input
                  {...getInputProps()}
                  accept=".pdf,.docs,.docx,.ppt"
                />
                <div className="flex items-center justify-center gap-2 p-2 rounded-full bg-[#ECF5FF]">
                  <div className="p-2 rounded-full bg-[#DDEBFC]">
                    <CloudUpload className="size-8 text-indigo-500" />
                  </div>
                </div>
              </div>
              <p className="text-sm text-indigo-500 font-semibold">
                Upload your resume in Doc, docx, pdf or txt
              </p>
            </div>

            <p className="text-sm italic text-center mt-6 text-indigo-500 font-semibold">
              Or choose a previous version
            </p>
          </>
        )}

        {currentJob?.resumes && currentJob?.resumes?.length > 0 && (
          <div className="border rounded-md overflow-hidden mt-8">
            <Table>
              <TableHeader className="bg-gray-300">
                <TableRow>
                  <TableHead className="font-bold text-black">
                    File name
                  </TableHead>
                  <TableHead className="font-bold text-black">
                    Version date
                  </TableHead>
                  <TableHead className="font-bold text-black">
                    Activate
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentJob.resumes.map((resume) => (
                  <TableRow key={`resume-file-${resume.id}`}>
                    <TableCell className="font-medium text-nowrap">
                      {getResumeFileName(
                        resume?.file_name ?? "",
                        userProfile?.email ?? "",
                      )}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell>
                      <Checkbox
                        checked={currentResumeId === resume.id}
                        onCheckedChange={(checked: boolean | "indeterminate") =>
                          handleCheckedChange(checked, resume.id)
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="flex items-center gap-4 justify-end absolute bottom-0 right-0">
        <Button
          disabled={isLoading}
          onClick={handleNext}
        >
          Next Step
        </Button>
      </div>
    </div>
  );
};

export default YourResume;
